import { z } from 'zod';

// Common validation patterns
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const nameRegex = /^[a-zA-Z\s\-']+$/;

// Helper: Validate individual fields
export const validateField = (schema, field, value) => {
  try {
    const fieldSchema = z.object({
      [field]: schema.shape[field]
    });
    fieldSchema.parse({ [field]: value });
    return null;
  } catch (error) {
    if (error.errors && error.errors.length > 0) {
      return error.errors[0].message;
    }
    return 'Invalid value';
  }
};

// 📘 Basic Information Schema
export const basicInfoSchema = z.object({
  fullName: z.string()
    .min(2, 'Name must be at least 2 characters long')
    .max(100, 'Name must not exceed 100 characters')
    .regex(nameRegex, 'Name can only contain letters, spaces, hyphens, and apostrophes')
    .refine(name => !name.includes('  '), 'Name cannot contain multiple consecutive spaces')
    .transform(name => name.trim()),

  email: z.string()
    .min(1, 'Email is required')
    .transform(email => email.toLowerCase().trim())
    .refine(email => {
      if (!emailRegex.test(email)) return false;
      const [_, domain] = email.includes('@') ? email.split('@') : [null, null];
      const typos = {
        'gmail.com': ['gamil.com', 'gmial.com', 'gmal.com'],
        'yahoo.com': ['yaho.com', 'yahooo.com'],
        'hotmail.com': ['hotmal.com', 'hotmai.com'],
        'outlook.com': ['outlok.com', 'outlock.com'],
      };
      for (const [correct, variants] of Object.entries(typos)) {
        if (variants.includes(domain)) return false;
      }
      return true;
    }, {
      message: (email) => {
        const [_, domain] = email.includes('@') ? email.split('@') : [null, null];
        const typos = {
          'gmail.com': ['gamil.com', 'gmial.com', 'gmal.com'],
          'yahoo.com': ['yaho.com', 'yahooo.com'],
          'hotmail.com': ['hotmal.com', 'hotmai.com'],
          'outlook.com': ['outlok.com', 'outlock.com'],
        };
        for (const [correct, variants] of Object.entries(typos)) {
          if (variants.includes(domain)) {
            return `Did you mean ${correct}?`;
          }
        }
        return 'Please enter a valid email address';
      }
    }),

  phone: z.string()
    .min(1, 'Phone number is required')
    .transform(phone => phone.replace(/\D/g, ''))
    .refine(phone => /^\d{10,15}$/.test(phone), {
      message: 'Please enter a valid phone number (10-15 digits)'
    }),

  gender: z.enum(['male', 'female', 'other'], {
    errorMap: () => ({ message: 'Please select a valid gender option' })
  }),

  dateOfBirth: z.string()
    .refine(val => {
      if (!val) return false;
      const date = new Date(val);
      return !isNaN(date.getTime());
    }, 'Please enter a valid date')
    .transform(val => new Date(val))
    .refine(date => {
      const today = new Date();
      const age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
        age--;
      }
      return age >= 18;
    }, 'You must be at least 18 years old to register as a doctor')
    .refine(date => date <= new Date(), 'Date of birth cannot be in the future'),

  address: z.string()
    .min(5, 'Address must be at least 5 characters long')
    .max(500, 'Address must not exceed 500 characters')
    .transform(addr => addr.trim()),
});

// 🧠 Professional Details Schema
export const professionalDetailsSchema = z.object({
  professionalType: z.enum([
    'psychiatrist',
    'clinical_psychologist',
    'counselor',
    'psychiatric_nurse',
    'psychiatric_social_worker'
  ], {
    errorMap: () => ({ message: 'Please select a valid professional type' })
  }),

  specializations: z.array(z.string())
    .min(1, 'At least one specialization is required'),

  qualifications: z.array(z.string())
    .min(1, 'At least one qualification is required'),

  experience: z.string()
    .min(1, 'Please select your years of experience'),

  languages: z.array(z.string())
    .min(1, 'At least one language is required'),

  bio: z.string()
    .min(50, 'Professional bio must be at least 50 characters long')
    .max(1000, 'Professional bio must not exceed 1000 characters')
    .transform(bio => bio.trim()),

  registrationNumber: z.string()
    .min(1, 'Registration number is required')
    .transform(val => val.trim()),

  registrationDate: z.date()
    .min(new Date(new Date().setFullYear(new Date().getFullYear() - 50)), 'Registration date cannot be more than 50 years ago')
    .max(new Date(), 'Registration date cannot be in the future'),

  registrationAuthority: z.string()
    .min(1, 'Registration authority is required')
    .transform(val => val.trim()),

  licenseNumber: z.string()
    .min(1, 'License number is required')
    .transform(val => val.trim()),

  licenseExpiryDate: z.date()
    .min(new Date(Date.now() + 60 * 1000), 'License expiry date must be in the future'),

  additionalCertifications: z.array(z.string()).optional(),
});

// 📄 Document Verification Schema
export const documentVerificationSchema = z.object({
  professionalType: z.enum([
    'psychiatrist',
    'clinical_psychologist',
    'counselor',
    'psychiatric_nurse',
    'psychiatric_social_worker'
  ], {
    errorMap: () => ({ message: 'Please select a valid professional type' })
  }),
  documents: z.record(z.string().url('Invalid document URL')),
});

// 🏥 Practice Setup Schema
export const practiceSetupSchema = z.object({
  consultationFee: z.number()
    .min(0, 'Consultation fee must be a positive number'),

  followUpFee: z.number()
    .min(0, 'Follow-up fee must be a positive number'),

  consultationTypes: z.array(z.string())
    .min(1, 'At least one consultation type is required'),

  professionalSummary: z.string()
    .min(50, 'Professional summary must be at least 50 characters long')
    .max(1000, 'Professional summary must not exceed 1000 characters'),

  availabilities: z.array(z.object({
    day: z.string(),
    startTime: z.string(),
    endTime: z.string(),
    slotDuration: z.number().min(15).max(120),
  })).min(1, 'Please set at least one working day and time slot'),
});

// 💳 Payment & Subscription Schema
export const paymentAndSubscriptionSchema = z.object({
  bankName: z.string().min(1, 'Bank name is required').transform(val => val.trim()),
  accountHolderName: z.string().min(1, 'Account holder name is required').transform(val => val.trim()),
  accountNumber: z.string().regex(/^\d{9,18}$/, 'Please enter a valid account number (9–18 digits)'),
  ifscCode: z.string().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Please enter a valid IFSC code'),
  panNumber: z.string().regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Please enter a valid PAN number'),
  paymentCycle: z.enum(['weekly', 'biweekly', 'monthly'], {
    errorMap: () => ({ message: 'Please select a valid payment cycle' })
  }),
  minimumPayout: z.number().min(0, 'Minimum payout must be a positive number'),
  selectedPlan: z.object({
    id: z.string(),
    name: z.string(),
    price: z.number(),
    features: z.array(z.string()),
  }),
});
